import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, MessagesSquare, Loader2, AlertTriangle, Clock, CreditCard, Brain, Mail, MessageCircle, MessageSquare, Instagram, RefreshCw, ArrowUpRight, Send, Sparkles, Bot } from "lucide-react";
import MessageAgent from "@/components/MessageAgent";
import MessageThread from "@/components/MessageThread";
import AssistantDesk from "@/components/AssistantDesk";

const sourceIcon = (s) => (s === "Gmail" ? Mail : s === "Instagram" ? Instagram : MessageCircle);
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const REPLY_CHANNELS = ["sms", "dm", "telegram"];

const Badge = ({ color, icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${color}1f`, color }}>
    {Icon && <Icon size={9} />}{children}
  </span>
);

export default function MessageCenter({ onClose, onOpenPerson, onTopUp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [remembering, setRemembering] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [imp, setImp] = useState(null);
  const [threadFor, setThreadFor] = useState(null);

  const load = () => { setLoading(true); api.messageCenter().then(setData).catch(() => setData(null)).finally(() => setLoading(false)); };
  const loadImport = () => api.imessageStatus().then(setImp).catch(() => setImp(null));
  useEffect(() => { load(); loadImport(); const t = setInterval(loadImport, 8000); return () => clearInterval(t); }, []);

  const sendReply = async (pid, channel) => {
    if (!replyText.trim()) return;
    setSending(true);
    const fn = channel === "telegram" ? api.telegramReply : channel === "sms" ? api.imessageReply : api.instagramReply;
    try { const r = await fn(pid, replyText.trim()); toast.success(r.message || "Sent"); setReplyFor(null); setReplyText(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const remember = async (pid) => {
    setRemembering(pid);
    try { const r = await api.rememberPerson(pid); toast.success(`Remembered ${r.facts.length} key details`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not build memory"); }
    finally { setRemembering(null); }
  };

  const list = tab === "credit" ? (data?.credit_watch || []) : tab === "all" ? (data?.threads || []) : (data?.priorities || []);

  return (
    <div className="max-w-5xl mx-auto pt-8 px-4 pb-24">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><MessagesSquare size={18} /> Message Command Center</h2>
        <button data-testid="messages-close-button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>
      <p className="text-zinc-500 font-mono text-xs mb-5">Every account in one place. LifeOS flags credit talk, catches missed replies, and remembers what matters.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 text-sm font-mono py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Scanning your conversations…</div>
      ) : !data ? (
        <div className="text-center text-zinc-500 py-16">Could not load the command center.</div>
      ) : (
        <>
          {!data.connected && (
            <div data-testid="messages-demo-notice" className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3 mb-4 text-[12px] text-amber-200/90 flex items-center gap-2">
              <AlertTriangle size={14} className="shrink-0" /> Showing a labeled demo workspace. Connect Gmail or iMessage and this fills with your real conversations (demo auto-hides).
            </div>
          )}

          {imp && imp.total_expected > imp.messages && (imp.total_expected - imp.messages) > 25 && (
            <div data-testid="messages-import-bar" className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.05] p-3 mb-4">
              <div className="flex items-center justify-between text-[12px] mb-1.5">
                <span className="flex items-center gap-1.5 text-cyan-200 font-semibold"><Loader2 size={13} className="animate-spin" /> Importing your messages…</span>
                <span className="font-mono text-cyan-200/80">{(imp.total_expected - imp.messages).toLocaleString()} left · {imp.messages.toLocaleString()} of ~{imp.total_expected.toLocaleString()}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 rounded-full transition-all duration-700" style={{ width: `${Math.min(99, Math.round((imp.messages / imp.total_expected) * 100))}%` }} /></div>
            </div>
          )}

          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { k: "people", label: "Conversations", value: data.stats.people, color: "#7cc4ff", icon: MessagesSquare },
              { k: "unanswered", label: "Need a reply", value: data.stats.unanswered, color: "#f5b301", icon: Clock },
              { k: "credit", label: "Credit mentions", value: data.stats.credit_mentions, color: "#ff6b8b", icon: CreditCard },
              { k: "sources", label: "Sources", value: Object.keys(data.stats.sources || {}).length, color: "#5ef2b0", icon: ArrowUpRight },
            ].map((s) => (
              <div key={s.k} data-testid={`messages-stat-${s.k}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono mb-1" style={{ color: s.color }}><s.icon size={12} />{s.label}</div>
                <div className="text-2xl font-tactical font-bold text-zinc-100">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-3">
            {[["priorities", "Priorities"], ["credit", "Credit watch"], ["all", "All"], ["agent", "AI Agent"], ["assistant", "Assistant"]].map(([k, label]) => (
              <button key={k} data-testid={`messages-tab-${k}`} onClick={() => setTab(k)} className={`text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-colors inline-flex items-center gap-1.5 ${tab === k ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{k === "agent" && <Sparkles size={12} />}{k === "assistant" && <Bot size={12} />}{label}</button>
            ))}
          </div>

          {tab === "agent" ? (
            <MessageAgent onTopUp={onTopUp} onReplied={load} />
          ) : tab === "assistant" ? (
            <AssistantDesk onTopUp={onTopUp} />
          ) : (
          <>
          <div className="space-y-2">
            {list.length === 0 ? (
              <div className="text-center text-zinc-600 text-sm py-12">{tab === "credit" ? "No one is talking about credit right now." : "Nothing here yet — connect an account to fill this up."}</div>
            ) : list.map((t) => {
              const Icon = sourceIcon(t.source);
              return (
                <div key={t.person_id} data-testid={`messages-thread-${t.person_id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 hover:border-cyan-300/25 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full grid place-items-center bg-white/5 border border-white/10 shrink-0"><Icon size={15} className="text-cyan-300" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-tactical font-bold text-sm text-zinc-100">{t.name}</span>
                        {t.company && <span className="text-[11px] text-zinc-500">· {t.company}</span>}
                        <Badge color="#7cc4ff" icon={Icon}>{t.source}</Badge>
                        {t.unanswered && <Badge color="#f5b301" icon={Clock}>needs reply</Badge>}
                        {t.credit && <Badge color="#ff6b8b" icon={CreditCard}>credit</Badge>}
                      </div>
                      <p className="text-[13px] text-zinc-400 mt-1 line-clamp-2">{t.last_direction === "outbound" ? "You: " : ""}{t.snippet}</p>
                      <div className="text-[10px] font-mono text-zinc-600 mt-1">{fmt(t.last_at)} · {t.message_count} message{t.message_count !== 1 ? "s" : ""}{t.credit_terms?.length ? ` · flagged: ${t.credit_terms.join(", ")}` : ""}</div>

                      {t.memory?.length > 0 && (
                        <div className="mt-2 rounded-lg bg-violet-500/[0.06] border border-violet-500/20 p-2">
                          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-violet-300 font-mono mb-1"><Brain size={10} /> Remember</div>
                          <ul className="space-y-0.5">{t.memory.map((f, i) => <li key={i} className="text-[11px] text-violet-100/90 leading-snug">• {f}</li>)}</ul>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {REPLY_CHANNELS.includes(t.channel) && (
                        <button data-testid={`messages-reply-${t.person_id}`} onClick={() => { setReplyFor(replyFor === t.person_id ? null : t.person_id); setReplyText(""); }} className="flex items-center gap-1 text-[10px] font-mono text-cyan-300 border border-cyan-400/30 rounded-lg px-2 py-1 hover:bg-cyan-500/15"><Send size={11} /> Reply</button>
                      )}
                      <button data-testid={`messages-remember-${t.person_id}`} onClick={() => remember(t.person_id)} disabled={remembering === t.person_id} title="Have LifeOS remember key details" className="flex items-center gap-1 text-[10px] font-mono text-violet-300 border border-violet-400/30 rounded-lg px-2 py-1 hover:bg-violet-500/15">
                        {remembering === t.person_id ? <Loader2 size={11} className="animate-spin" /> : <Brain size={11} />} {t.memory?.length ? "Update" : "Remember"}
                      </button>
                      <button data-testid={`messages-open-${t.person_id}`} onClick={() => setThreadFor(t.person_id)} className="flex items-center gap-1 text-[10px] font-mono text-cyan-300 border border-cyan-400/30 rounded-lg px-2 py-1 hover:bg-cyan-500/15"><MessageSquare size={11} /> View chat</button>
                    </div>
                  </div>
                  {replyFor === t.person_id && (
                    <div data-testid={`messages-reply-box-${t.person_id}`} className="mt-2 anim-slidein flex items-end gap-2">
                      <textarea data-testid={`messages-reply-input-${t.person_id}`} rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} maxLength={1000} placeholder={`Reply to ${t.name} on ${t.source}…`} className="flex-1 bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-pink-400/40 resize-none" />
                      <button data-testid={`messages-reply-send-${t.person_id}`} onClick={() => sendReply(t.person_id, t.channel)} disabled={sending} className="h-9 px-3 rounded-lg bg-pink-500 hover:bg-pink-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-1">{sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button data-testid="messages-refresh" onClick={load} className="mt-5 flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-cyan-300"><RefreshCw size={13} /> Refresh</button>
          </>
          )}
        </>
      )}
      {threadFor && <MessageThread pid={threadFor} onClose={() => setThreadFor(null)} onReplied={load} />}
    </div>
  );
}
